import { Outlet } from 'react-router-dom';
import { TopBar } from './TopBar';
import { useUiStore } from '../../store/uiStore';

const STEPS: { number: 1 | 2 | 3 | 4 | 5; label: string }[] = [
  { number: 1, label: 'Project Details' },
  { number: 2, label: 'Applications' },
  { number: 3, label: 'Virtual Machines' },
  { number: 4, label: 'Sizing Config' },
  { number: 5, label: 'Results & BOM' },
];

const styles = {
  root: {
    minHeight: '100vh',
    backgroundColor: '#021526',
    color: '#e2e8f0',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  // Push content below the fixed TopBar (56px)
  below: {
    paddingTop: '56px',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100vh',
  },
  wizardNav: {
    borderBottom: '1px solid rgba(76,92,104,0.35)',
    backgroundColor: '#132840',
    padding: '0 24px',
  },
  stepList: {
    display: 'flex',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    gap: 0,
  },
  main: {
    flex: 1,
    padding: '32px 24px',
    maxWidth: '1280px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box' as const,
  },
};

function stepItemStyle(active: boolean, completed: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    color: active ? '#F5A621' : completed ? '#CEECF8' : '#4C5C68',
    borderBottom: active ? '2px solid #F5A621' : '2px solid transparent',
    cursor: 'default',
    userSelect: 'none',
    transition: 'color 0.15s, border-color 0.15s',
    whiteSpace: 'nowrap',
  };
}

function stepBadgeStyle(active: boolean, completed: boolean): React.CSSProperties {
  return {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    flexShrink: 0,
    backgroundColor: active
      ? 'rgba(245,166,33,0.12)'
      : completed
        ? 'rgba(206,236,248,0.12)'
        : '#132840',
    color: active ? '#F5A621' : completed ? '#CEECF8' : '#4C5C68',
    border: active
      ? '1px solid rgba(245,166,33,0.5)'
      : completed
        ? '1px solid rgba(206,236,248,0.2)'
        : '1px solid rgba(76,92,104,0.35)',
  };
}

export function Shell() {
  const currentStep = useUiStore(s => s.currentStep);
  const toast = useUiStore(s => s.toast);
  const clearToast = useUiStore(s => s.clearToast);

  return (
    <div style={styles.root}>
      <TopBar />
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 500,
            backgroundColor: '#166534',
            color: '#dcfce7',
            padding: '10px 18px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 500,
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          ✓ {toast}
          <button
            type="button"
            onClick={clearToast}
            aria-label="Dismiss notification"
            style={{
              background: 'none',
              border: 'none',
              color: '#86efac',
              fontSize: '14px',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '0 2px',
            }}
          >
            ×
          </button>
        </div>
      )}
      <div style={styles.below}>
        {/* Wizard step indicator */}
        <nav style={styles.wizardNav} aria-label="Wizard steps">
          <ol style={styles.stepList}>
            {STEPS.map(step => {
              const active = step.number === currentStep;
              const completed = step.number < currentStep;
              return (
                <li key={step.number}>
                  <div style={stepItemStyle(active, completed)} aria-current={active ? 'step' : undefined}>
                    <span style={stepBadgeStyle(active, completed)}>
                      {completed ? '✓' : step.number}
                    </span>
                    {step.label}
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Page content */}
        <main style={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
